/**
 * Agency feature — data-fetching hooks.
 *
 * public.organizations is read via supabase.schema("public") (same pattern as
 * OrganizationContext.tsx). relay.* tables use the `as never` cast so the
 * default relay schema applies without fighting TypeScript's generated types
 * (same pattern as features/plan/api.ts and features/inbox/api.ts).
 *
 * All mutating RPCs (relay_agency_create_client, relay_agency_assign_plan)
 * are called via supabase.schema("public").rpc(...) since they live in the
 * public schema per P5-T2 (the RPCs are in public, not relay).
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type {
  AgencyClientSummary,
  AgencyClientOrg,
  AgencyClientSubscription,
  AgencyClientPendingInvite,
  AgencyRollupRow,
  AgencyRollupLeadRow,
  ResellerPlan,
  CreateAgencyClientInput,
  UpsertResellerPlanInput,
} from "./types";
import {
  DEFAULT_AGENCY_CLIENT_LIST_SORT,
  getAgencyClientOrgOrderClauses,
  isAgencyClientJoinedSort,
  sortAgencyClients,
  type AgencyClientListSort,
} from "./agency-client-list-sort";

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Access relay-schema tables without fighting TS generated types. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function relayFrom(table: string): any {
  return supabase.from(table as never);
}

/** RPC helper that routes through the public schema — matches how
 *  useCreateRelayTenant calls its RPC. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function publicRpc(fn: string, args: Record<string, unknown>): Promise<{ data: any; error: any }> {
  const client = supabase.schema("public") as unknown as {
    rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: unknown }>;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return client.rpc(fn, args) as Promise<{ data: any; error: any }>;
}

/** RPC helper for relay-schema functions (agency_rollup, agency_rollup_leads).
 *  The client's default schema is "relay", so a plain rpc() resolves there;
 *  the cast bypasses generated types that don't know the new functions yet. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function relayRpc(fn: string, args: Record<string, unknown>): Promise<{ data: any; error: any }> {
  const client = supabase as unknown as {
    rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: unknown }>;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return client.rpc(fn, args) as Promise<{ data: any; error: any }>;
}

type PublicOrganizationsResult = {
  data: unknown[] | null;
  error: unknown;
};

interface PublicOrganizationsQuery extends PromiseLike<PublicOrganizationsResult> {
  eq(column: string, value: string): PublicOrganizationsQuery;
  order(
    column: string,
    options: { ascending: boolean; nullsFirst: boolean },
  ): PublicOrganizationsQuery;
  range(from: number, to: number): PublicOrganizationsQuery;
}

const AGENCY_CLIENTS_PAGE_SIZE = 200;

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const AGENCY_QUERY_KEYS = {
  clients: (agencyOrgId: string) => ["agency", "clients", agencyOrgId] as const,
  clientsList: (agencyOrgId: string, sort: AgencyClientListSort) =>
    ["agency", "clients", agencyOrgId, sort] as const,
  plans: (agencyOrgId: string) => ["agency", "plans", agencyOrgId] as const,
  orgType: (orgId: string) => ["agency", "org-type", orgId] as const,
  rollup: (agencyOrgId: string) => ["agency", "rollup", agencyOrgId] as const,
  rollupLeads: (agencyOrgId: string, limit: number) =>
    ["agency", "rollup-leads", agencyOrgId, limit] as const,
};

// ---------------------------------------------------------------------------
// useAgencyClients — list client orgs under an agency with subscription info
// ---------------------------------------------------------------------------

/**
 * Lists all client orgs under the given agency, joined with their active
 * subscription and plan name. Uses two parallel queries (public.organizations
 * for the orgs, relay.agency_client_subscriptions + relay.reseller_plans for
 * subscription status) and joins in JS, avoiding PostgREST cross-schema join
 * limitations.
 */
export function useAgencyClients(
  agencyOrgId: string | null,
  sort: AgencyClientListSort = DEFAULT_AGENCY_CLIENT_LIST_SORT,
) {
  return useQuery({
    queryKey: AGENCY_QUERY_KEYS.clientsList(agencyOrgId ?? "", sort),
    enabled: !!agencyOrgId,
    staleTime: 2 * 60 * 1_000,
    queryFn: async (): Promise<AgencyClientSummary[]> => {
      if (!agencyOrgId) return [];

      // 1. Client orgs from public.organizations — additive RLS lets agency owners SELECT these.
      // Cast to bypass generated types: org_type + parent_org_id were added in P5-T1 migration
      // but types haven't been regenerated yet (same pattern as relay-schema `as never` cast).
      const publicOrgClient = supabase.schema("public") as unknown as {
        from: (table: string) => {
          select: (cols: string) => {
            eq: (col: string, val: string) => PublicOrganizationsQuery;
          };
        };
      };
      const buildOrgQuery = () => {
        let orgQuery = publicOrgClient
          .from("organizations")
          .select("id, name, slug, org_type, parent_org_id, org_function")
          .eq("parent_org_id", agencyOrgId)
          .eq("org_type", "client");

        for (const clause of getAgencyClientOrgOrderClauses(sort)) {
          orgQuery = orgQuery.order(clause.column, {
            ascending: clause.ascending,
            nullsFirst: clause.nullsFirst,
          });
        }

        return orgQuery;
      };

      const orgs: AgencyClientOrg[] = [];

      for (let from = 0; ; from += AGENCY_CLIENTS_PAGE_SIZE) {
        const to = from + AGENCY_CLIENTS_PAGE_SIZE - 1;
        const { data: orgRows, error: orgError } = await buildOrgQuery().range(from, to);

        if (orgError) throw orgError;

        const page = (orgRows ?? []) as AgencyClientOrg[];
        orgs.push(...page);

        if (page.length < AGENCY_CLIENTS_PAGE_SIZE) break;
      }

      if (orgs.length === 0) return [];

      const clientIds = orgs.map((o) => o.id);

      // 2. Active subscriptions from relay.agency_client_subscriptions
      const { data: subRows, error: subError } = await relayFrom("agency_client_subscriptions")
        .select("id, agency_org_id, client_org_id, reseller_plan_id, status, created_at")
        .eq("agency_org_id", agencyOrgId)
        .in("client_org_id", clientIds);

      if (subError) throw subError;
      const subs = (subRows ?? []) as AgencyClientSubscription[];

      // 3. Reseller plans for name lookup
      const planIds = [...new Set(subs.map((s) => s.reseller_plan_id).filter(Boolean))];
      let plans: ResellerPlan[] = [];
      if (planIds.length > 0) {
        const { data: planRows, error: planError } = await relayFrom("reseller_plans")
          .select("id, name")
          .in("id", planIds);
        if (planError) throw planError;
        plans = (planRows ?? []) as ResellerPlan[];
      }

      const planById = new Map(plans.map((p) => [p.id, p]));
      const subByClient = new Map(subs.map((s) => [s.client_org_id, s]));

      const clients = orgs.map((org) => {
        const sub = subByClient.get(org.id) ?? null;
        const plan = sub?.reseller_plan_id ? planById.get(sub.reseller_plan_id) : undefined;
        return {
          org,
          subscription: sub,
          planName: plan?.name ?? null,
          planStatus: sub?.status ?? null,
        };
      });

      return isAgencyClientJoinedSort(sort)
        ? sortAgencyClients(clients, sort)
        : clients;
    },
  });
}

// ---------------------------------------------------------------------------
// useCreateAgencyClient — provision a new client org via RPC
// ---------------------------------------------------------------------------

export function useCreateAgencyClient() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (
      input: CreateAgencyClientInput & { agency_org_id: string },
    ): Promise<string> => {
      const { data, error } = await publicRpc("relay_agency_create_client", {
        p_agency_org: input.agency_org_id,
        p_name: input.name,
        p_slug: input.slug,
        p_owner_email: input.owner_email,
        ...(input.plan ? { p_plan: input.plan } : {}),
      });
      if (error) throw error;
      const clientId = data as string;

      // relay_agency_create_client only inserts the organization_invites row —
      // it does not deliver it (mirrors relay_platform_admin_create_tenant, see
      // useCreateRelayTenant.ts). Non-fatal: the client org exists even if the
      // email can't be sent yet (e.g. RESEND_API_KEY unset) — it can be resent.
      const { error: inviteError } = await supabase.functions.invoke("relay-send-invite", {
        body: { organization_id: clientId, email: input.owner_email, role: "owner" },
      });
      if (inviteError) {
        console.warn("[useCreateAgencyClient] owner invite email failed", inviteError);
      }
      return clientId;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: AGENCY_QUERY_KEYS.clients(variables.agency_org_id) });
    },
  });
}

// ---------------------------------------------------------------------------
// useAgencyClientInvite / useResendAgencyClientInvite — pending owner invite
// ---------------------------------------------------------------------------

/**
 * Fetches the most recent unaccepted owner invite for a client org, so the
 * agency client detail page can surface pending-invite status and offer a
 * resend action. Routed through the relay_agency_get_client_invite RPC
 * (20260717140000) rather than a direct table SELECT: that RPC returns only
 * display columns (never organization_invites.token), whereas an RLS SELECT
 * policy on the table would expose the raw token to any caller requesting
 * it — a bearer credential that supabase/functions/relay-invite-signup
 * accepts on its own to create a pre-confirmed account as the invite's
 * email, with no proof the caller controls that email.
 */
export function useAgencyClientInvite(clientOrgId: string | null) {
  return useQuery({
    queryKey: ["agency", "client-invite", clientOrgId ?? ""],
    enabled: !!clientOrgId,
    staleTime: 30 * 1_000,
    queryFn: async (): Promise<AgencyClientPendingInvite | null> => {
      if (!clientOrgId) return null;
      const { data, error } = await publicRpc("relay_agency_get_client_invite", {
        p_client_org: clientOrgId,
      });
      if (error) throw error;
      const rows = (data ?? []) as AgencyClientPendingInvite[];
      return rows[0] ?? null;
    },
  });
}

/**
 * (Re)sends the owner invite email for a client org via relay-send-invite.
 * Authorized for agency owners/admins managing the org's parent agency
 * (see the auth extension in supabase/functions/relay-send-invite/index.ts).
 */
export function useResendAgencyClientInvite(clientOrgId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { email: string; role: string }): Promise<void> => {
      if (!clientOrgId) throw new Error("clientOrgId required");
      const { error } = await supabase.functions.invoke("relay-send-invite", {
        body: { organization_id: clientOrgId, email: input.email, role: input.role },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agency", "client-invite", clientOrgId ?? ""] });
    },
  });
}

// ---------------------------------------------------------------------------
// useResellerPlans — list all reseller plans for an agency org
// ---------------------------------------------------------------------------

export function useResellerPlans(agencyOrgId: string | null) {
  return useQuery({
    queryKey: AGENCY_QUERY_KEYS.plans(agencyOrgId ?? ""),
    enabled: !!agencyOrgId,
    staleTime: 2 * 60 * 1_000,
    queryFn: async (): Promise<ResellerPlan[]> => {
      if (!agencyOrgId) return [];

      const { data, error } = await relayFrom("reseller_plans")
        .select(
          [
            "id", "agency_org_id", "name", "slug", "price_cents", "interval",
            "max_contacts", "max_seats", "max_email_sends_month",
            "max_automations", "max_sequences",
            "feature_gates", "is_active", "created_at",
          ].join(", "),
        )
        .eq("agency_org_id", agencyOrgId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      return (data ?? []) as ResellerPlan[];
    },
  });
}

// ---------------------------------------------------------------------------
// useUpsertResellerPlan — insert or update a reseller plan
// ---------------------------------------------------------------------------

export function useUpsertResellerPlan() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (
      input: UpsertResellerPlanInput & { agency_org_id: string },
    ): Promise<ResellerPlan> => {
      const row = {
        agency_org_id: input.agency_org_id,
        name: input.name,
        slug: input.slug,
        price_cents: input.price_cents,
        interval: input.interval,
        max_contacts: input.max_contacts,
        max_seats: input.max_seats,
        max_email_sends_month: input.max_email_sends_month,
        max_automations: input.max_automations,
        max_sequences: input.max_sequences,
        feature_gates: input.feature_gates,
        is_active: input.is_active,
      };

      let result;
      if (input.id) {
        // Update
        result = await relayFrom("reseller_plans")
          .update(row)
          .eq("id", input.id)
          .select()
          .single();
      } else {
        // Insert
        result = await relayFrom("reseller_plans")
          .insert(row)
          .select()
          .single();
      }

      const { data, error } = result;
      if (error) throw error;
      return data as ResellerPlan;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: AGENCY_QUERY_KEYS.plans(variables.agency_org_id) });
    },
  });
}

// ---------------------------------------------------------------------------
// useDeleteResellerPlan
// ---------------------------------------------------------------------------

export function useDeleteResellerPlan() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: { id: string; agency_org_id: string }): Promise<void> => {
      const { error } = await relayFrom("reseller_plans")
        .delete()
        .eq("id", input.id)
        .eq("agency_org_id", input.agency_org_id);
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: AGENCY_QUERY_KEYS.plans(variables.agency_org_id) });
    },
  });
}

// ---------------------------------------------------------------------------
// useAssignPlan — call relay_agency_assign_plan RPC
// ---------------------------------------------------------------------------

export function useAssignPlan() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      client_org_id: string;
      reseller_plan_id: string;
      agency_org_id: string;
    }): Promise<void> => {
      const { error } = await publicRpc("relay_agency_assign_plan", {
        p_client_org: input.client_org_id,
        p_reseller_plan: input.reseller_plan_id,
      });
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: AGENCY_QUERY_KEYS.clients(variables.agency_org_id) });
    },
  });
}

// ---------------------------------------------------------------------------
// useClientFeatureGates — read feature_gates for a client org
// ---------------------------------------------------------------------------

/**
 * Fetches feature_gates for a client org's tenant_plan_limits row.
 * Agency owners can SELECT client plan limits via the additive RLS policy
 * that grants them client-org read access.
 */
export function useClientFeatureGates(clientOrgId: string | null) {
  return useQuery({
    queryKey: ["agency", "client-gates", clientOrgId ?? ""],
    enabled: !!clientOrgId,
    queryFn: async (): Promise<Record<string, boolean>> => {
      if (!clientOrgId) return {};
      const { data, error } = await relayFrom("tenant_plan_limits")
        .select("feature_gates")
        .eq("organization_id", clientOrgId)
        .maybeSingle();
      if (error) throw error;
      return (data as { feature_gates: Record<string, boolean> | null } | null)
        ?.feature_gates ?? {};
    },
  });
}

// ---------------------------------------------------------------------------
// useSetClientFeatureGate — RPC to toggle a gate on a client org
// ---------------------------------------------------------------------------

export function useSetClientFeatureGate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      agency_org_id: string;
      client_org_id: string;
      gate_key: string;
      enabled: boolean;
    }): Promise<void> => {
      const { error } = await publicRpc("relay_agency_set_client_feature_gate", {
        p_agency_org: input.agency_org_id,
        p_client_org: input.client_org_id,
        p_gate_key: input.gate_key,
        p_enabled: input.enabled,
      });
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: AGENCY_QUERY_KEYS.clients(variables.agency_org_id) });
      qc.invalidateQueries({ queryKey: ["agency", "client-gates", variables.client_org_id] });
    },
  });
}

// ---------------------------------------------------------------------------
// Agency client session RPCs
// ---------------------------------------------------------------------------

export function useStartClientSession() {
  return useMutation({
    mutationFn: async (input: {
      agency_org_id: string;
      client_org_id: string;
    }): Promise<string> => {
      const { data, error } = await publicRpc("relay_agency_start_client_session", {
        p_agency_org: input.agency_org_id,
        p_client_org: input.client_org_id,
      });
      if (error) throw error;
      return data as string; // session UUID
    },
  });
}

export function useEndClientSession() {
  return useMutation({
    mutationFn: async (sessionId: string): Promise<void> => {
      const { error } = await publicRpc("relay_agency_end_client_session", {
        p_session_id: sessionId,
      });
      if (error) throw error;
    },
  });
}

// ---------------------------------------------------------------------------
// useSetClientFunction — RPC to set org_function for a client org (P2)
// ---------------------------------------------------------------------------

export function useSetClientFunction() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      agency_org_id: string;
      client_org_id: string;
      org_function: string;
    }): Promise<void> => {
      const { error } = await publicRpc("relay_agency_set_client_function", {
        p_agency_org: input.agency_org_id,
        p_client_org: input.client_org_id,
        p_function: input.org_function,
      });
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: AGENCY_QUERY_KEYS.clients(variables.agency_org_id) });
    },
  });
}

// ---------------------------------------------------------------------------
// Portfolio rollup — relay.agency_rollup() / relay.agency_rollup_leads()
// ---------------------------------------------------------------------------

/**
 * Cross-client CRM KPI rollup for the agency's whole scope: direct
 * parent_org_id children PLUS relay.agency_rollup_members mappings and their
 * children (the Glorify umbrella). Server-side gated — non-admins get [].
 *
 * PostgREST can serialize bigint/numeric loosely, so counts are coerced with
 * Number() before they reach the UI.
 */
export function useAgencyRollup(agencyOrgId: string | null) {
  return useQuery({
    queryKey: AGENCY_QUERY_KEYS.rollup(agencyOrgId ?? ""),
    enabled: !!agencyOrgId,
    staleTime: 60 * 1_000,
    queryFn: async (): Promise<AgencyRollupRow[]> => {
      if (!agencyOrgId) return [];
      const { data, error } = await relayRpc("agency_rollup", { _agency: agencyOrgId });
      if (error) throw error;
      return ((data ?? []) as AgencyRollupRow[]).map((r) => ({
        ...r,
        contacts: Number(r.contacts) || 0,
        open_deals: Number(r.open_deals) || 0,
        open_pipeline: Number(r.open_pipeline) || 0,
      }));
    },
  });
}

/** Combined recent-leads feed across the agency's rollup scope. */
export function useAgencyRollupLeads(agencyOrgId: string | null, limit = 50) {
  return useQuery({
    queryKey: AGENCY_QUERY_KEYS.rollupLeads(agencyOrgId ?? "", limit),
    enabled: !!agencyOrgId,
    staleTime: 60 * 1_000,
    queryFn: async (): Promise<AgencyRollupLeadRow[]> => {
      if (!agencyOrgId) return [];
      const { data, error } = await relayRpc("agency_rollup_leads", {
        _agency: agencyOrgId,
        _limit: limit,
      });
      if (error) throw error;
      return (data ?? []) as AgencyRollupLeadRow[];
    },
  });
}
